import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { plural, pluralizeIssues } from '../../../../shared/helpers';
import type { ResolutionReason, VulnIgnoreReason } from '../../../../shared/issuesV2';
import {
  Category,
  ExceptionIgnoreScope,
  ExceptionPackageScope,
  IgnoredKind,
  VulnIgnoreReasons,
} from '../../../../shared/issuesV2';
import { useAddToast } from '../../../contexts/ToastContext';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { BUTTON_SIZE, BUTTON_TYPE, ButtonGroup, ErrorAlert, InlineLoaderButton, LabeledTextArea } from '../../generic';
import Button from '../../generic/Button';
import Flex from '../../generic/Flex';
import useClearErrorsOnUnmount from './hooks/useClearErrorsOnUnmount';
import IgnoreWithExceptionsModal from './IgnoreWithExceptionsModal';

type IssueExceptionOnIgnore = {
  ignoredKind: IgnoredKind.IssueException;
  resolutionReason?: ResolutionReason;
  notes?: string;
  ignoreScope: ExceptionIgnoreScope;
  packageScope: ExceptionPackageScope;
};

type OnIgnoreParams =
  | {
      ignoredKind: Omit<IgnoredKind, 'issueException'>;
      resolutionReason?: ResolutionReason;
      notes?: string;
    }
  | IssueExceptionOnIgnore;

const isIssueExceptionIgnore = (ignoreScope: OnIgnoreParams): ignoreScope is IssueExceptionOnIgnore =>
  ignoreScope.ignoredKind === IgnoredKind.IssueException;

/**
 * Check if an issue exception action qualifies as a simple ignore:
 * 1. Affects a project or release group
 * 2. Affects a specific version
 */
const isSimpleIgnoreAction = (action: IssueExceptionOnIgnore): boolean => {
  // must be scoped to a Project or ReleaseGroup
  if (![ExceptionIgnoreScope.Project, ExceptionIgnoreScope.ReleaseGroup].includes(action.ignoreScope)) {
    return false;
  }
  // must only affect a specific package version
  return action.packageScope === ExceptionPackageScope.SelectedVersion;
};

type IgnoreIssuesModalBaseProps = {
  show: boolean;
  selectedIssuesCount: number;
  onHide: () => void;
};

type IgnoreIssuesModalProps = IgnoreIssuesModalBaseProps & {
  onIgnore: (ignoreProps: { ignoredKind: IgnoredKind; notes: string; resolutionReason?: ResolutionReason }) => void;
};

/**
 * IgnoreQualityIssuesModal represents the UI for the modal for ignoring issues. This is only used for Quality Issues at the moment
 */
const IgnoreQualityIssuesModal: VoidFunctionComponent<IgnoreIssuesModalProps> = ({
  show,
  selectedIssuesCount,
  onHide,
  onIgnore,
}) => {
  const { loading: isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);

  const [notes, setNotes] = useState<string>('');

  const onIgnoreClick = () =>
    onIgnore({
      ignoredKind: IgnoredKind.Manual,
      notes,
    });

  const onEnter = () => {
    setNotes('');
  };

  //  Ensure that when the component unmounts, we remove any error messages
  useClearErrorsOnUnmount();

  return (
    <Modal
      backdrop="static"
      onEscapeKeyDown={onHide}
      animation
      show={show}
      onEnter={onEnter}
      onHide={onHide}
      className="issues-modal"
    >
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">
          {`Ignore ${pluralizeIssues(selectedIssuesCount)}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <Flex direction="column" justify="flex-start" align="flex-start">
          <span className="issues-modal__body__message">
            {`${selectedIssuesCount.toLocaleString()} ${pluralizeIssues(selectedIssuesCount)} will be ignored.`}
          </span>
          <div className="issues-modal__body__notes">
            <LabeledTextArea
              label={`Add a note describing why ${plural(selectedIssuesCount, 'this is', 'these are')} ignored:`}
              onChange={setNotes}
              isDisabled={isLoading}
              value={notes}
              placeholder="Type your note..."
            />
          </div>
        </Flex>
        {errors && (
          <div className="issues-modal__body__errors">
            <ErrorAlert status={{ errors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} isDisabled={isLoading} onClick={onHide}>
            Cancel
          </Button>
          <InlineLoaderButton
            type={BUTTON_TYPE.PRIMARY}
            size={BUTTON_SIZE.MEDIUM}
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading}
            onClick={onIgnoreClick}
          >
            Ignore
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Container component for the ignore modal for use on the IssuesInbox.
 * For the inbox, after ignoring we need to re-fetch the issues and the status summary.
 */
export const IgnoreIssuesModalInboxContainer: VoidFunctionComponent<IgnoreIssuesModalBaseProps> = ({
  show,
  selectedIssuesCount,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const areVulnerabilityIssues = issueCategory === Category.Vulnerability;

  const onIgnore = async (scope: OnIgnoreParams) => {
    const shouldIncludeReason =
      areVulnerabilityIssues &&
      scope.resolutionReason &&
      Object.values(VulnIgnoreReasons).includes(scope.resolutionReason);

    const reason = shouldIncludeReason ? (scope.resolutionReason as VulnIgnoreReason) : undefined;

    // Perform the IssueExceptions action if the scope has the proper typing and
    // the action isn't a simple ignore.
    if (isIssueExceptionIgnore(scope) && !isSimpleIgnoreAction(scope)) {
      dispatch(
        IssuesV2Store.actions.createIssueExceptionsForSelectedIssues(
          scope.ignoreScope,
          scope.packageScope,
          scope.notes,
          reason,
        ),
      );
      // Default case to perform normal Issues V2 ignoring if it's not IssueExceptions.
    } else {
      dispatch(IssuesV2Store.actions.ignoreSelectedIssues(scope.notes, reason));
    }

    onHide();
    //  Display a toast letting the user know the action was successful.
    addToast({
      type: 'success',
      content: `Ignored ${pluralizeIssues(selectedIssuesCount)}.`,
    });
  };

  // Issue exceptions are limited to only licensing and vulnerability issues at the moment.
  if (issueCategory === 'licensing' || issueCategory === 'vulnerability') {
    return (
      <IgnoreWithExceptionsModal
        show={show}
        selectedIssuesCount={selectedIssuesCount}
        onHide={onHide}
        onIgnore={onIgnore}
      />
    );
  }

  return (
    <IgnoreQualityIssuesModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onIgnore={onIgnore}
    />
  );
};

type IgnoreIssuesModalDetailsProps = IgnoreIssuesModalBaseProps & {
  id: number;
};

/**
 * Container component for the ignore modal for use on the IssueDetails.
 */
export const IgnoreIssuesModalDetailsContainer: VoidFunctionComponent<IgnoreIssuesModalDetailsProps> = ({
  id,
  show,
  selectedIssuesCount,
  onHide,
}) => {
  const addToast = useAddToast();
  const dispatch = useDispatch();
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);

  const onIgnore = async (scope: OnIgnoreParams) => {
    if (isIssueExceptionIgnore(scope) && !isSimpleIgnoreAction(scope)) {
      dispatch(
        IssuesV2Store.actions.createIssueExceptions(
          { issueIds: [id] },
          scope.ignoreScope,
          scope.packageScope,
          scope.notes,
        ),
      );
    } else {
      dispatch(IssuesV2Store.actions.ignoreIssues({ issueIds: [id] }, scope.notes, scope.resolutionReason));
    }
    //  This handles either closing the drawer (if on the inbox) or
    //  re-fetching the issue details (if we're on the full-page).
    dispatch(IssuesV2Store.actions.refetchIssueDetailsAfterAction(id));

    //  Hide the Ignore modal
    onHide();
    //  Display a toast letting the user know the action was successful.
    addToast({
      type: 'success',
      content: `Ignored ${pluralizeIssues(selectedIssuesCount)}.`,
    });
  };

  // Issue exceptions are limited to only licensing and vulnerability issues at the moment.
  if (issueCategory === Category.Licensing || issueCategory === Category.Vulnerability) {
    return (
      <IgnoreWithExceptionsModal
        show={show}
        selectedIssuesCount={selectedIssuesCount}
        onHide={onHide}
        onIgnore={onIgnore}
      />
    );
  }

  return (
    <IgnoreQualityIssuesModal
      show={show}
      selectedIssuesCount={selectedIssuesCount}
      onHide={onHide}
      onIgnore={onIgnore}
    />
  );
};
